import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaBars, FaSignOutAlt, FaTimes } from "react-icons/fa";

const SESSION_NAV_ITEMS = [
  { key: "game", label: "Game", path: "game" },
  { key: "wallets", label: "Session", path: "wallets" },
  { key: "players", label: "Players", path: "players" },
  { key: "dealers", label: "Staff", path: "dealers" },
];

const BACK_OFFICE_NAV_ITEMS = [
  { key: "admin", label: "Admin", path: "admin" },
  { key: "dashboard", label: "Dashboard", path: "dashboard" },
  { key: "referrals", label: "Referrals", path: "referrals" },
  { key: "book", label: "Book", path: "book" },
  { key: "schedule", label: "Schedule", path: "schedule" },
  { key: "data", label: "Data", path: "data" },
];

const TOURNAMENT_NAV_ITEMS = [
  { key: "tournaments", label: "Tournaments", path: "tournamentsHome" },
  { key: "points", label: "Points", path: "tournamentPoints" },
  { key: "dashboard", label: "Dashboard", path: "tournamentDashboard" },
  { key: "players", label: "Players", path: "tournamentPlayers" },
];

function SessionStatus({ currSession }) {
  if (currSession === null) return "";

  return currSession ? (
    <span className="game-session-status game-session-status--active">
      Active Session
    </span>
  ) : (
    <span className="game-session-status game-session-status--inactive">
      No Active Session
    </span>
  );
}

export function BackOfficeHeader({
  activePage,
  cardroom,
  centerTitle,
  title,
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const mobileTitle = centerTitle || title;

  const navigateTo = (path) => {
    navigate(`/${cardroom}/${path}`);
  };

  const closeMenuAndNavigate = (path) => {
    navigateTo(path);
    setIsMenuOpen(false);
  };

  const logOut = () => {
    localStorage.removeItem("token");
    navigate("/");
  };

  return (
    <>
      <header className="game-header">
        <div className="game-header__container">
          <div className="game-header__left">
            <button
              className="game-hamburger-btn"
              type="button"
              onClick={() => setIsMenuOpen(true)}
              aria-label="Open navigation menu"
            >
              <FaBars />
            </button>
            <h1 className="game-title">{title}</h1>
          </div>

          <div className="game-header__center">
            <h1 className="game-title">{mobileTitle}</h1>
          </div>

          <nav className="game-header__desktop-nav" aria-label="Back office navigation">
            {BACK_OFFICE_NAV_ITEMS.map((item) => (
              <button
                key={item.key}
                className={`game-nav__btn ${
                  activePage === item.key ? "game-nav__btn--active" : ""
                }`}
                type="button"
                onClick={() => navigateTo(item.path)}
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="game-header__right">
            <button
              className="game-btn game-btn--secondary"
              type="button"
              onClick={() => navigateTo("game")}
            >
              Game Operations
            </button>
            <button
              className="game-btn game-btn--ghost game-header__desktop-nav"
              type="button"
              onClick={logOut}
            >
              Log Out
            </button>
          </div>
        </div>
      </header>

      {isMenuOpen && (
        <>
          <div
            className="game-drawer-overlay"
            onClick={() => setIsMenuOpen(false)}
          />
          <div className="game-drawer">
            <div className="game-drawer-header">
              <h2 className="game-title">Menu</h2>
              <button
                className="game-icon-btn"
                type="button"
                onClick={() => setIsMenuOpen(false)}
                aria-label="Close navigation menu"
              >
                <FaTimes />
              </button>
            </div>
            <nav className="game-drawer-nav" aria-label="Mobile back office navigation">
              {BACK_OFFICE_NAV_ITEMS.map((item) => (
                <button
                  key={item.key}
                  className={`game-drawer-item ${
                    activePage === item.key ? "game-drawer-item--active" : ""
                  }`}
                  type="button"
                  onClick={() => closeMenuAndNavigate(item.path)}
                >
                  {item.label}
                </button>
              ))}
            </nav>
            <div className="game-drawer-footer">
              <button
                className="game-drawer-admin"
                type="button"
                onClick={() => closeMenuAndNavigate("game")}
              >
                Game Console
              </button>
              <button
                className="game-drawer-logout"
                type="button"
                onClick={logOut}
              >
                <FaSignOutAlt /> Log Out
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export function TournamentHeader({
  activePage = "tournaments",
  cardroom,
  centerTitle,
  currSession,
  title,
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const mobileTitle = centerTitle || title;
  const permission = localStorage.getItem("permission") || "";
  const dealerMode = Number(localStorage.getItem("dealer_mode") || 0);
  const tournamentOnly = permission.includes("Y");

  const navigateTo = (path) => {
    navigate(`/${cardroom}/${path}`);
  };

  const closeMenuAndNavigate = (path) => {
    navigateTo(path);
    setIsMenuOpen(false);
  };

  const logOut = () => {
    localStorage.removeItem("token");
    navigate("/");
  };

  return (
    <>
      <header className="game-header">
        <div className="game-header__container">
          <div className="game-header__left">
            <button
              className="game-hamburger-btn"
              type="button"
              onClick={() => setIsMenuOpen(true)}
              aria-label="Open navigation menu"
            >
              <FaBars />
            </button>
            <h1 className="game-title">{title}</h1>
            <SessionStatus currSession={currSession} />
          </div>

          <div className="game-header__center">
            <h1 className="game-title">{mobileTitle}</h1>
            <SessionStatus currSession={currSession} />
          </div>

          <nav className="game-header__desktop-nav" aria-label="Tournament navigation">
            {TOURNAMENT_NAV_ITEMS.map((item) => (
              <button
                key={item.key}
                className={`game-nav__btn ${
                  activePage === item.key ? "game-nav__btn--active" : ""
                }`}
                type="button"
                disabled={!item.path}
                onClick={() => item.path && navigateTo(item.path)}
                style={!item.path ? { cursor: "default", opacity: 0.6 } : undefined}
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="game-header__right">
            {!tournamentOnly && (
              <button
                className="game-btn game-btn--secondary"
                type="button"
                onClick={() => navigateTo("game")}
              >
                Game Operations
              </button>
            )}
            {dealerMode === 1 && (
              <button
                className="game-btn game-btn--secondary"
                type="button"
                onClick={() => navigateTo("dealerdashboard")}
              >
                Dealer
              </button>
            )}
            <button
              className="game-btn game-btn--ghost game-header__desktop-nav"
              type="button"
              onClick={logOut}
            >
              Log Out
            </button>
          </div>
        </div>
      </header>

      {isMenuOpen && (
        <>
          <div
            className="game-drawer-overlay"
            onClick={() => setIsMenuOpen(false)}
          />
          <div className="game-drawer">
            <div className="game-drawer-header">
              <h2 className="game-title">Menu</h2>
              <button
                className="game-icon-btn"
                type="button"
                onClick={() => setIsMenuOpen(false)}
                aria-label="Close navigation menu"
              >
                <FaTimes />
              </button>
            </div>
            <nav className="game-drawer-nav" aria-label="Mobile tournament navigation">
              {TOURNAMENT_NAV_ITEMS.map((item) => (
                <button
                  key={item.key}
                  className={`game-drawer-item ${
                    activePage === item.key ? "game-drawer-item--active" : ""
                  }`}
                  type="button"
                  disabled={!item.path}
                  onClick={() => item.path && closeMenuAndNavigate(item.path)}
                  style={!item.path ? { cursor: "default", opacity: 0.6 } : undefined}
                >
                  {item.label}
                </button>
              ))}
            </nav>
            <div className="game-drawer-footer">
              {!tournamentOnly && (
                <button
                  className="game-drawer-admin"
                  type="button"
                  onClick={() => closeMenuAndNavigate("game")}
                >
                  Game Operations
                </button>
              )}
              {dealerMode === 1 && (
                <button
                  className="game-drawer-admin"
                  type="button"
                  onClick={() => closeMenuAndNavigate("dealerdashboard")}
                >
                  Dealer
                </button>
              )}
              <button
                className="game-drawer-logout"
                type="button"
                onClick={logOut}
              >
                <FaSignOutAlt /> Log Out
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default function GameSessionHeader({
  activePage,
  cardroom,
  currSession,
  dealerMode,
  permission,
  title,
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  const navigateTo = (path) => {
    navigate(`/${cardroom}/${path}`);
  };

  const closeMenuAndNavigate = (path) => {
    navigateTo(path);
    setIsMenuOpen(false);
  };

  const logOut = () => {
    localStorage.removeItem("token");
    navigate("/");
  };

  return (
    <>
      <header className="game-header">
        <div className="game-header__container">
          <div className="game-header__left">
            <button
              className="game-hamburger-btn"
              type="button"
              onClick={() => setIsMenuOpen(true)}
              aria-label="Open navigation menu"
            >
              <FaBars />
            </button>
            <h1 className="game-title">{title}</h1>
            <SessionStatus currSession={currSession} />
          </div>

          <div className="game-header__center">
            <h1 className="game-title">{title}</h1>
            <SessionStatus currSession={currSession} />
          </div>

          <nav className="game-header__desktop-nav" aria-label="Session navigation">
            {SESSION_NAV_ITEMS.map((item) => (
              <button
                key={item.key}
                className={`game-nav__btn ${
                  activePage === item.key ? "game-nav__btn--active" : ""
                }`}
                type="button"
                onClick={() => navigateTo(item.path)}
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="game-header__right">
            {(permission?.includes("O") || permission?.includes("Y")) && (
              <button
                className="game-btn game-btn--secondary"
                type="button"
                onClick={() => navigateTo("tournamentsHome")}
              >
                Tournaments
              </button>
            )}
            {permission?.includes("V") && (
              <button
                className="game-btn game-btn--secondary"
                type="button"
                onClick={() => navigateTo("admin")}
              >
                Admin
              </button>
            )}
            {dealerMode === 1 && (
              <button
                className="game-btn game-btn--secondary"
                type="button"
                onClick={() => navigateTo("DealerDashboard")}
              >
                Dealer
              </button>
            )}
            <button
              className="game-btn game-btn--ghost game-header__desktop-nav"
              type="button"
              onClick={logOut}
            >
              Log Out
            </button>
          </div>
        </div>
      </header>

      {isMenuOpen && (
        <>
          <div
            className="game-drawer-overlay"
            onClick={() => setIsMenuOpen(false)}
          />
          <div className="game-drawer">
            <div className="game-drawer-header">
              <h2 className="game-title">Menu</h2>
              <button
                className="game-icon-btn"
                type="button"
                onClick={() => setIsMenuOpen(false)}
                aria-label="Close navigation menu"
              >
                <FaTimes />
              </button>
            </div>
            <nav className="game-drawer-nav" aria-label="Mobile session navigation">
              {SESSION_NAV_ITEMS.map((item) => (
                <button
                  key={item.key}
                  className={`game-drawer-item ${
                    activePage === item.key ? "game-drawer-item--active" : ""
                  }`}
                  type="button"
                  onClick={() => closeMenuAndNavigate(item.path)}
                >
                  {item.label}
                </button>
              ))}
            </nav>
            <div className="game-drawer-footer">
              {(permission?.includes("O") || permission?.includes("Y")) && (
                <button
                  className="game-drawer-admin"
                  type="button"
                  onClick={() => closeMenuAndNavigate("tournamentsHome")}
                >
                  Tournaments
                </button>
              )}
              {permission?.includes("V") && (
                <button
                  className="game-drawer-admin"
                  type="button"
                  onClick={() => closeMenuAndNavigate("admin")}
                >
                  Admin
                </button>
              )}
              {dealerMode === 1 && (
                <button
                  className="game-drawer-admin"
                  type="button"
                  onClick={() => closeMenuAndNavigate("DealerDashboard")}
                >
                  Dealer
                </button>
              )}
              <button
                className="game-drawer-logout"
                type="button"
                onClick={logOut}
              >
                <FaSignOutAlt /> Log Out
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
